import React from 'react';

interface LoadingSpinnerProps {
  /** Optional message shown beneath the loader. */
  message?: string;
  /** When true, fills the viewport. When false, sizes to content. Default true. */
  fullScreen?: boolean;
}

/**
 * Branded loading indicator: the Trigon "tm" mark at center, breathing softly,
 * with two counter-rotating orbital arcs in the brand orange and primary
 * graphite. Designed to feel deliberate and on-brand rather than generic.
 *
 * Uses inline @keyframes for animations Tailwind doesn't ship by default
 * (orbital sweeps, breath pulse). Respects `prefers-reduced-motion`.
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  message,
  fullScreen = true,
}) => {
  const wrapperClass = fullScreen
    ? 'min-h-screen flex flex-col items-center justify-center gap-6 bg-gradient-to-br from-primary-50 via-white to-brand-50'
    : 'flex flex-col items-center justify-center gap-4 py-12';

  return (
    <div className={wrapperClass} role="status" aria-live="polite">
      <style>{loaderKeyframes}</style>

      <div className="trigon-loader">
        {/* Soft glow halo */}
        <div className="trigon-loader__halo" aria-hidden="true" />

        {/* Outer orbit — brand orange arc, sweeping clockwise */}
        <svg
          className="trigon-loader__orbit trigon-loader__orbit--outer"
          viewBox="0 0 100 100"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="trigon-orbit-outer" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#cc4f1a" stopOpacity="0" />
              <stop offset="60%" stopColor="#cc4f1a" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#e67e22" stopOpacity="1" />
            </linearGradient>
          </defs>
          <circle
            cx="50"
            cy="50"
            r="44"
            fill="none"
            stroke="url(#trigon-orbit-outer)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeDasharray="80 200"
          />
        </svg>

        {/* Inner orbit — graphite arc, sweeping counter-clockwise */}
        <svg
          className="trigon-loader__orbit trigon-loader__orbit--inner"
          viewBox="0 0 100 100"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="trigon-orbit-inner" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#3a3a3a" stopOpacity="0" />
              <stop offset="60%" stopColor="#3a3a3a" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#5f6368" stopOpacity="1" />
            </linearGradient>
          </defs>
          <circle
            cx="50"
            cy="50"
            r="34"
            fill="none"
            stroke="url(#trigon-orbit-inner)"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeDasharray="50 160"
          />
        </svg>

        {/* Brand mark — gently breathing */}
        <img
          src="/img/logo.png"
          alt=""
          className="trigon-loader__mark"
          aria-hidden="true"
        />
      </div>

      {message && (
        <div className="trigon-loader__message">
          <span className="text-sm font-medium text-primary-700">{message}</span>
          <span className="trigon-loader__dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        </div>
      )}

      <span className="sr-only">{message || 'Loading'}</span>
    </div>
  );
};

const loaderKeyframes = `
  @keyframes trigon-spin-cw {
    to { transform: rotate(360deg); }
  }
  @keyframes trigon-spin-ccw {
    to { transform: rotate(-360deg); }
  }
  @keyframes trigon-breath {
    0%, 100% { transform: scale(0.92); opacity: 0.85; }
    50%      { transform: scale(1.04); opacity: 1; }
  }
  @keyframes trigon-halo {
    0%, 100% { opacity: 0.35; transform: scale(0.95); }
    50%      { opacity: 0.7;  transform: scale(1.1); }
  }
  @keyframes trigon-dot {
    0%, 80%, 100% { transform: translateY(0);   opacity: 0.3; }
    40%           { transform: translateY(-3px); opacity: 1;  }
  }

  .trigon-loader {
    position: relative;
    width: 140px;
    height: 140px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .trigon-loader__halo {
    position: absolute;
    inset: 10%;
    border-radius: 9999px;
    background: radial-gradient(
      circle at center,
      rgba(204, 79, 26, 0.25) 0%,
      rgba(204, 79, 26, 0.08) 45%,
      rgba(204, 79, 26, 0) 70%
    );
    filter: blur(8px);
    animation: trigon-halo 2.4s ease-in-out infinite;
  }

  .trigon-loader__orbit {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    transform-origin: 50% 50%;
  }
  .trigon-loader__orbit--outer {
    animation: trigon-spin-cw 1.8s linear infinite;
  }
  .trigon-loader__orbit--inner {
    animation: trigon-spin-ccw 1.4s linear infinite;
  }

  .trigon-loader__mark {
    position: relative;
    width: 56px;
    height: auto;
    object-fit: contain;
    filter: drop-shadow(0 2px 8px rgba(204, 79, 26, 0.25));
    animation: trigon-breath 2s ease-in-out infinite;
  }

  .trigon-loader__message {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
  }
  .trigon-loader__dots {
    display: inline-flex;
    gap: 3px;
  }
  .trigon-loader__dots span {
    width: 4px;
    height: 4px;
    border-radius: 9999px;
    background: #cc4f1a;
    animation: trigon-dot 1.4s ease-in-out infinite;
  }
  .trigon-loader__dots span:nth-child(2) { animation-delay: 0.2s; }
  .trigon-loader__dots span:nth-child(3) { animation-delay: 0.4s; }

  @media (prefers-reduced-motion: reduce) {
    .trigon-loader__halo,
    .trigon-loader__orbit--outer,
    .trigon-loader__orbit--inner,
    .trigon-loader__mark,
    .trigon-loader__dots span {
      animation: none;
    }
    .trigon-loader__halo { opacity: 0.5; }
  }
`;

export default LoadingSpinner;
